import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { MemberAuthenticationConfiguration, Settings } from '../_models/settings';
import { NavigationEnd, Router } from '@angular/router';
import { filter, first, map } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';
import * as fromRoot from '../store/reducers';
import * as deeplink from '@app/shared/utils/browser-deeplink';
import { SchemaInterface } from '@app/shared/types/schema-interface';
import { Meta } from '@angular/platform-browser';
import { LOYALTY_CLUB_METADATA_SELECTOR } from '@app/core/consts';
import { BootstrapLocaleService } from '@app/core/settings/language/bootstrap-locale.service';
import { LanguageSettingsService } from '@app/core/settings/language/language-settings.service';
import { LoadSettings } from '../store/core-context/core-context-actions';

interface Contact {
  type: 'email' | 'phone';
  value: string;
}

@Injectable()
export class SettingsService {
  constructor(private router: Router,
              private boostrapLocaleService: BootstrapLocaleService,
              private languageSettings: LanguageSettingsService,
              private store: Store<fromRoot.State>,
              private meta: Meta,
  ) {}

  public init(): void {
    this.store.dispatch(new LoadSettings());
  }

  public getMinimumMemberAge(): Observable<number> {
    return this.getSettingsOnce().pipe(
      map(settings => settings.minimum_member_age),
    );
  }

  public getSuggestedMsisdnCountry(): Observable<string | null> {
    return this.getSettingsOnce().pipe(
      map(settings => settings.suggestedMsisdnCountry()),
    );
  }

  public getSuggestedMsisdnCountryDialCode(): Observable<string | null> {
    return this.getSettingsOnce().pipe(
      map(settings => settings.suggestedMsisdnCountryDialCode()),
    );
  }

  public getAvailableMsisdnCountryCallingCodes(): Observable<string[]> {
    return this.getSettingsOnce().pipe(
      map((settings) => settings.getAvailableMsisdnCountryCodes()),
    );
  }

  public getMemberAuthenticationConfiguration(): Observable<MemberAuthenticationConfiguration | null> {
    return this.getSettingsOnce().pipe(
      map(settings => settings.member_authentication_configuration),
    );
  }

  public getTransactionsCurrency(): Observable<string | null> {
    return this.getSettingsOnce().pipe(
      map(settings => settings.transactions_currency),
    );
  }

  public getHasAdditionalIndexHtml(): Observable<boolean> {
    return this.getSettingsOnce().pipe(
      map(settings => !!settings.additional_index_html),
    );
  }

  public getContact(): Observable<Contact | null> {
    return this.getSettingsOnce().pipe(
      map(settings => {
        if (settings.contact_email) {
          return { type: 'email', value: settings.contact_email };
        } else if (settings.contact_phone) {
          return { type: 'phone', value: settings.contact_phone };
        }
        return null;
      }),
    );
  }

  public getSlug(): Observable<string> {
    return this.getSettingsOnce().pipe(
      map((settings) => settings.slug),
    );
  }
  /**
   * Get current settings.
   */
  public getSettingsOnce(): Observable<Settings> {
    return this.store.pipe(
      select(fromRoot.selectCoreContext),
      map((s) => s.settings),
      filter((v) => !!v),
      first(),
    );
  }

  public isGaleriaDominikanska(): Observable<boolean> {
    return this.getSettingsOnce().pipe(map((settings) => settings.slug === 'galeria-dominikanska'));
  }

  public getSettingsFromMetaTag(): Settings {
    const rawSettings = this.meta.getTag(LOYALTY_CLUB_METADATA_SELECTOR)?.dataset?.settings;
    if (!rawSettings) {
      throw new Error('No club settings found in meta tag');
    }
    const settings = new Settings(JSON.parse(rawSettings));
    this.handleSettingsCallbacks(settings);
    return settings;
  }

  private handleSettingsCallbacks(settings: Settings): void {
    this.setupHtmlTags(settings.custom_js);
    this.setLanguage(settings.schema);
    this.handleMobileApp(settings);
  }

  private setLanguage(schema: SchemaInterface): void {
    this.boostrapLocaleService.init();

    const language = this.languageSettings.getLanguage({
      availableLanguages: schema.languages,
      defaultLanguage: schema.default_language,
    });
    this.languageSettings.setLanguage(language);
  }


  /**
   * Special hack for running custom JS. DO NOT EVER ADD CUSTOM JS BOX. TRUST ME
   */
  private trackEventsAndInjectCustomJs(customJs: string): void {

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => eval(customJs));
    eval(customJs);
  }

  private setupHtmlTags(customJS: string): void {
    if (customJS) { this.trackEventsAndInjectCustomJs(customJS); }
  }

  private handleMobileApp(settings: Settings): void {
    if (!settings.mobile_app_setting) { return; }

    deeplink.setup({
      android: {
        appId: settings.mobile_app_setting.android_app_id,
        storeUrl: settings.androidStoreLink(),
      },
      iOS: {
        appId: settings.mobile_app_setting.ios_app_id,
        storeUrl: settings.iosStoreLink(),
      },
    });
  }
}
